import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  MagnifyingGlass,
  ArrowsClockwise,
  HeartStraight,
  Trash,
  X,
} from "@phosphor-icons/react";
import ConfirmDialog from "./ConfirmDialog";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: "new", label: "New" },
  { value: "matched", label: "Matched" },
  { value: "acknowledged", label: "Acknowledged" },
  { value: "closed", label: "Closed" },
];
const STATUS_LABEL = Object.fromEntries(STATUS_OPTIONS.map((s) => [s.value, s.label]));
const STATUS_PILL = {
  new: "bg-amber-100 text-amber-800",
  matched: "bg-blue-100 text-blue-800",
  acknowledged: "bg-emerald-100 text-emerald-800",
  closed: "bg-ink-200 text-ink-700",
};
const GIFT_LABEL = { in_memory: "In Memory Of", in_honor: "In Honor Of" };

const fmtDateTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
};

const Th = ({ children }) => (
  <th className="px-4 py-3 text-left text-[10px] uppercase tracking-[0.15em] font-semibold">
    {children}
  </th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-4 py-3 align-top ${className}`}>{children}</td>
);

const StatCard = ({ label, value, testId }) => (
  <div
    data-testid={testId}
    className="rounded-2xl border border-ink-100 bg-white px-4 py-3.5"
  >
    <div className="text-[10px] uppercase tracking-[0.2em] text-ink-500 font-semibold">{label}</div>
    <div className="mt-1 font-heading text-2xl text-ink-900">{value ?? "—"}</div>
  </div>
);

const SponsorIntentsAdmin = ({ onDataChange }) => {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const refreshStats = useCallback(() => {
    axios.get(`${API_BASE}/admin/sponsor-intents/stats`, { withCredentials: true })
      .then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (status !== "all") params.status = status;
      if (q.trim()) params.q = q.trim();
      const [l, s] = await Promise.all([
        axios.get(`${API_BASE}/admin/sponsor-intents`, { params, withCredentials: true }),
        axios.get(`${API_BASE}/admin/sponsor-intents/stats`, { withCredentials: true }),
      ]);
      setItems(l.data || []);
      setStats(s.data);
    } catch { toast.error("Could not load sponsor intents."); }
    finally { setLoading(false); }
  }, [status, q]);

  useEffect(() => { load(); }, [status, load]);

  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(items.map((i) => i.id));
      const next = new Set();
      let changed = false;
      prev.forEach((id) => { if (visible.has(id)) next.add(id); else changed = true; });
      return changed ? next : prev;
    });
  }, [items]);

  const updateSelected = async (patch) => {
    if (!selected) return;
    setSaving(true);
    try {
      const res = await axios.patch(`${API_BASE}/admin/sponsor-intents/${selected.id}`, patch, { withCredentials: true });
      setSelected(res.data);
      setItems((prev) => prev.map((r) => r.id === res.data.id ? res.data : r));
      refreshStats(); onDataChange?.(); toast.success("Updated.");
    } catch { toast.error("Could not save."); }
    finally { setSaving(false); }
  };

  const toggleRow = (id) => setSelectedIds((prev) => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const clearSelection = () => setSelectedIds(new Set());
  const allVisibleSelected = items.length > 0 && items.every((i) => selectedIds.has(i.id));
  const toggleAllVisible = () =>
    allVisibleSelected ? clearSelection() : setSelectedIds(new Set(items.map((i) => i.id)));

  const askDeleteSingle = (id) => setConfirmDelete({ mode: "single", ids: [id] });
  const askDeleteBulk = () => selectedIds.size && setConfirmDelete({ mode: "bulk", ids: Array.from(selectedIds) });

  const doDelete = async () => {
    if (!confirmDelete) return;
    const { mode, ids } = confirmDelete;
    setDeleting(true);
    try {
      if (mode === "single") {
        await axios.delete(`${API_BASE}/admin/sponsor-intents/${ids[0]}`, { withCredentials: true });
        setItems((p) => p.filter((r) => r.id !== ids[0]));
        setSelectedIds((p) => { const n = new Set(p); n.delete(ids[0]); return n; });
        if (selected?.id === ids[0]) setSelected(null);
        toast.success("Intent deleted");
      } else {
        const res = await axios.post(`${API_BASE}/admin/sponsor-intents/bulk-delete`, { ids }, { withCredentials: true });
        const removed = new Set(ids);
        setItems((p) => p.filter((r) => !removed.has(r.id)));
        clearSelection();
        const n = res.data?.deleted ?? ids.length;
        toast.success(`${n} intent${n === 1 ? "" : "s"} deleted`);
      }
      refreshStats(); onDataChange?.(); setConfirmDelete(null);
    } catch (err) {
      if ([401, 403].includes(err?.response?.status)) toast.error("Session ended. Please sign in again.");
      else if (err?.response?.status === 404) { toast.error("Record no longer exists."); load(); setConfirmDelete(null); }
      else toast.error("Could not delete.");
    } finally { setDeleting(false); }
  };

  return (
    <section className="space-y-6" data-testid="sponsor-intents-admin">
      <div className="flex items-start gap-3">
        <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <HeartStraight size={22} weight="duotone" />
        </span>
        <div>
          <h2 className="font-heading text-2xl text-ink-900 font-medium leading-tight">Sponsor a Book Intents</h2>
          <p className="mt-1 text-sm text-ink-600 max-w-2xl">
            Memorial and honor bookplate requests captured before donors continue to the giving page.
            Match to the incoming PayPal donation manually and update status.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Total" value={stats?.total} testId="si-stat-total" />
        <StatCard label="New" value={stats?.by_status?.new} testId="si-stat-new" />
        <StatCard label="Matched" value={stats?.by_status?.matched} testId="si-stat-matched" />
        <StatCard label="Acknowledged" value={stats?.by_status?.acknowledged} testId="si-stat-ack" />
      </div>

      <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[220px]">
          <label className="block text-[10px] uppercase tracking-[0.18em] text-ink-500 mb-1">Search</label>
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
            <input type="search" value={q} onChange={(e) => setQ(e.target.value)}
              data-testid="si-search"
              placeholder="Honoree, donor, email, placement…"
              className="w-full rounded-full border border-ink-200 bg-white pl-10 pr-3 py-2 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none" />
          </div>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.18em] text-ink-500 mb-1">Status</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)}
            data-testid="si-filter-status"
            className="rounded-full border border-ink-200 bg-white px-3 py-2 text-sm">
            <option value="all">All</option>
            {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <button type="submit" data-testid="si-search-submit" className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary-dark">Apply</button>
        <button type="button" onClick={() => { setQ(""); setStatus("all"); }}
          data-testid="si-search-reset"
          className="inline-flex items-center gap-1 rounded-full border border-ink-200 bg-white px-3 py-2 text-xs font-semibold text-ink-600 hover:bg-ink-50">
          <ArrowsClockwise size={12} weight="bold" />Reset
        </button>
      </form>

      {selectedIds.size > 0 && (
        <div data-testid="si-bulk-bar" className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-red-200 bg-red-50/70 px-4 py-2.5">
          <div className="text-sm text-ink-800">
            <strong data-testid="si-bulk-count">{selectedIds.size}</strong> selected
            <span className="text-ink-500"> (of {items.length} shown)</span>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={clearSelection} data-testid="si-bulk-clear"
              className="rounded-full border border-ink-200 bg-white px-3 py-1.5 text-xs font-semibold text-ink-700 hover:bg-ink-50">Clear selection</button>
            <button type="button" onClick={askDeleteBulk} data-testid="si-bulk-delete"
              className="inline-flex items-center gap-1.5 rounded-full bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700">
              <Trash size={12} weight="bold" />Delete Selected
            </button>
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-ink-100 bg-white overflow-x-auto">
        <table className="min-w-full text-sm" data-testid="si-table">
          <thead className="bg-softblue/40 text-ink-700">
            <tr>
              <th className="px-3 py-3 w-8">
                <input type="checkbox" checked={allVisibleSelected} onChange={toggleAllVisible}
                  aria-label="Select all visible" data-testid="si-select-all"
                  className="h-4 w-4 accent-primary cursor-pointer" />
              </th>
              <Th>Date</Th><Th>Type</Th><Th>Honoree</Th><Th>Donor</Th><Th>Email</Th><Th>Placement</Th><Th>Status</Th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-ink-500 text-sm">
                {loading ? "Loading..." : "No sponsor intents match the current filters."}
              </td></tr>
            ) : items.map((r) => (
              <tr key={r.id} data-testid={`si-row-${r.id}`}
                onClick={() => setSelected(r)}
                className={`border-t border-ink-100 hover:bg-softblue/20 cursor-pointer ${selectedIds.has(r.id) ? "bg-red-50/40" : ""}`}>
                <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" checked={selectedIds.has(r.id)} onChange={() => toggleRow(r.id)}
                    aria-label={`Select intent from ${r.donor_name}`}
                    data-testid={`si-select-${r.id}`}
                    className="h-4 w-4 accent-primary cursor-pointer" />
                </td>
                <Td className="whitespace-nowrap text-ink-600 text-xs">{fmtDateTime(r.created_at)}</Td>
                <Td className="text-ink-800 text-xs">{GIFT_LABEL[r.gift_type] || r.gift_type}</Td>
                <Td className="font-semibold text-ink-900">{r.honoree_name}</Td>
                <Td className="text-ink-700">{r.donor_name}</Td>
                <Td className="text-ink-700 text-xs">{r.donor_email}</Td>
                <Td className="text-ink-600 text-xs max-w-[200px]">{r.preferred_placement || "—"}</Td>
                <Td>
                  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${STATUS_PILL[r.status] || STATUS_PILL.new}`}>
                    {STATUS_LABEL[r.status] || "New"}
                  </span>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <DetailDrawer intent={selected} onClose={() => setSelected(null)}
          onUpdate={updateSelected} onDelete={() => askDeleteSingle(selected.id)} saving={saving} />
      )}

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        title={confirmDelete?.mode === "bulk"
          ? `Delete ${confirmDelete.ids.length} selected intent${confirmDelete.ids.length === 1 ? "" : "s"}?`
          : "Delete this intent?"}
        description={confirmDelete?.mode === "bulk"
          ? `This will permanently remove ${confirmDelete.ids.length} intent${confirmDelete.ids.length === 1 ? "" : "s"} and cannot be undone.`
          : "This will permanently remove this intent. This action cannot be undone."}
        confirmLabel={confirmDelete?.mode === "bulk"
          ? `Delete ${confirmDelete.ids.length} intent${confirmDelete.ids.length === 1 ? "" : "s"}`
          : "Delete Intent"}
        busy={deleting}
        onConfirm={doDelete}
        onCancel={() => setConfirmDelete(null)}
        testId="si-confirm-delete"
      />
    </section>
  );
};

const DetailDrawer = ({ intent: b, onClose, onUpdate, onDelete, saving }) => {
  const [notes, setNotes] = useState(b.internal_notes || "");
  const [ref, setRef] = useState(b.paypal_reference || "");
  useEffect(() => { setNotes(b.internal_notes || ""); setRef(b.paypal_reference || ""); }, [b.id]);
  return (
    <div className="fixed inset-0 z-40 flex justify-end" role="dialog" aria-modal="true" data-testid="si-detail-drawer">
      <button type="button" aria-label="Close" onClick={onClose} className="absolute inset-0 bg-ink-900/40" />
      <div className="relative w-full max-w-md bg-white h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-ink-100 px-5 py-4 flex items-center justify-between">
          <h3 className="font-heading text-lg text-ink-900 font-medium">Sponsor Intent Details</h3>
          <button type="button" onClick={onClose} data-testid="si-detail-close"
            className="inline-flex h-8 w-8 items-center justify-center rounded-full text-ink-500 hover:bg-ink-100" aria-label="Close">
            <X size={16} weight="bold" />
          </button>
        </div>
        <div className="p-5 space-y-4 text-sm">
          {[
            ["Received", fmtDateTime(b.created_at)],
            ["Gift Type", GIFT_LABEL[b.gift_type] || b.gift_type],
            ["Honoree", b.honoree_name],
            ["Bookplate", b.bookplate_text],
            ["Donor", b.donor_name],
            ["Donor Email", b.donor_email],
            ["Notify Recipient", b.notify_recipient ? "Yes" : "No"],
          ].map(([k, v]) => (
            <div key={k}>
              <div className="text-[10px] uppercase tracking-[0.18em] text-ink-500">{k}</div>
              <div className="text-ink-900">{v || "—"}</div>
            </div>
          ))}
          {b.notify_recipient && (
            <>
              {b.recipient_name && (<div><div className="text-[10px] uppercase tracking-[0.18em] text-ink-500">Recipient Name</div><div className="text-ink-800">{b.recipient_name}</div></div>)}
              {b.recipient_email && (<div><div className="text-[10px] uppercase tracking-[0.18em] text-ink-500">Recipient Email</div><div className="text-ink-800">{b.recipient_email}</div></div>)}
              {b.recipient_mailing_address && (<div><div className="text-[10px] uppercase tracking-[0.18em] text-ink-500">Recipient Address</div><div className="text-ink-800 whitespace-pre-wrap">{b.recipient_mailing_address}</div></div>)}
            </>
          )}
          {b.preferred_placement && (<div><div className="text-[10px] uppercase tracking-[0.18em] text-ink-500">Preferred Placement</div><div className="text-ink-800">{b.preferred_placement}</div></div>)}
          {b.note && (<div><div className="text-[10px] uppercase tracking-[0.18em] text-ink-500">Donor Note</div><p className="whitespace-pre-wrap text-ink-800 border border-ink-100 rounded-xl p-3 bg-paper/60">{b.note}</p></div>)}

          <div className="border-t border-ink-100 pt-4">
            <div className="text-[10px] uppercase tracking-[0.18em] text-ink-500 mb-1">Status</div>
            <select value={b.status || "new"} onChange={(e) => onUpdate({ status: e.target.value })}
              disabled={saving} data-testid="si-detail-status"
              className="w-full rounded-xl border border-ink-200 bg-white px-3 py-2 text-sm">
              {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-ink-500 mb-1">PayPal Reference / Transaction ID</div>
            <input type="text" value={ref} onChange={(e) => setRef(e.target.value)}
              data-testid="si-detail-ref"
              placeholder="e.g. 7X123..." className="w-full rounded-xl border border-ink-200 bg-white px-3 py-2 text-sm" />
            <button type="button" onClick={() => onUpdate({ paypal_reference: ref })}
              disabled={saving} data-testid="si-detail-save-ref"
              className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-1.5 text-xs font-semibold text-white hover:bg-primary-dark disabled:opacity-60">Save reference</button>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-ink-500 mb-1">Internal Notes (Admin-only)</div>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
              rows={4} data-testid="si-detail-notes"
              className="w-full rounded-xl border border-ink-200 bg-white px-3 py-2 text-sm"
              placeholder="Private notes never shown to the donor" />
            <button type="button" onClick={() => onUpdate({ internal_notes: notes })}
              disabled={saving} data-testid="si-detail-save-notes"
              className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-1.5 text-xs font-semibold text-white hover:bg-primary-dark disabled:opacity-60">Save notes</button>
          </div>

          <div className="mt-4 border-t border-ink-100 pt-4">
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-ink-500">Danger zone</div>
            <button type="button" onClick={onDelete} disabled={saving}
              data-testid="si-detail-delete"
              className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-red-300 bg-white px-4 py-2 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-60">
              <Trash size={12} weight="bold" />Delete intent
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SponsorIntentsAdmin;
